import { atom } from 'recoil';
import type { ConnectorPatientManifest } from '~/domain/persistence/ConnectorPatientManifest';
import type { OpenSelection } from './resolveOpenIntent';
import type { SeriesStreamPlan } from './streamingPlan';

/**
 * A retrieval completed on the worklist, waiting for the viewer to parse and
 * load it. The seam carries the retrieved bytes plus the patient's connector
 * identity: the files stay anonymous (named by ordinal, see `dataRetrieval`),
 * while the manifest carries the database row ids and load-time version tokens
 * the save workflow needs — identity the parsed DICOM alone cannot provide.
 * The streaming plan does carry DICOM UIDs, because the viewer has to address
 * the remaining slices on the wire with them. All of it lives only in memory
 * and is never logged.
 */
export type PendingViewerOpen = {
    /** One `.dcm` per retrieved instance, in the order requested. */
    files: File[];
    /**
     * Instances the retrieval did not obtain. Already accepted by the user;
     * the viewer repeats it once the patient is on screen.
     */
    skipped: number;
    /**
     * Image series whose geometry came from a manifest and whose pixels are
     * still to be fetched. Empty for a fully blocking open — the fallback
     * every progressive failure lands on.
     */
    streaming: SeriesStreamPlan[];
    /**
     * Connector identity + version tokens captured when the patient detail
     * was fetched for this open. Absent when the detail lacked usable
     * identity — the session then loads as "local only" (no saving).
     */
    connectorManifest?: ConnectorPatientManifest;
    /**
     * What the open resolved to: the workflow to land in and the objects to
     * select, as surrogate ids and nothing else.
     *
     * Resolved once, on the list, and handed over — never re-derived here.
     * A viewer that decides for itself lands somewhere other than the place
     * the user was promised, which is exactly what the concept prototype did
     * until the resolution was passed explicitly.
     */
    intent?: OpenSelection;
};

/**
 * Set by the worklist immediately before it navigates, consumed and cleared
 * by the viewer-side loader on mount.
 *
 * No `dangerouslyAllowMutability`, deliberately: Recoil's deep freeze is a
 * no-op on a `File` (its data lives in internal slots), so the files still
 * read back byte for byte. Only true because the seam carries `File`s; an
 * `ImportResult` here would freeze live repository objects irreversibly.
 *
 * The streaming plan survives the same freeze: an `ImageSeriesManifest` is
 * immutable by construction, and Recoil skips `ArrayBuffer` views, so its
 * `vec2`/`vec3`/`mat3` geometry is left alone rather than frozen out from
 * under the grid builder.
 */
export const pendingViewerOpenState = atom<PendingViewerOpen | null>({
    key: 'pendingViewerOpen',
    default: null,
});
